import { useState, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { ArrowRight, ArrowLeft, Check } from 'lucide-react';
import AddressAutocompleteInput from '../shared/AddressAutocompleteInput';

const INDUSTRIES = [
  { value: 'hair_salon', label: 'Hair Salon / Barbershop' },
  { value: 'spa', label: 'Spa & Wellness' },
  { value: 'dental', label: 'Dental Office' },
  { value: 'medical', label: 'Medical Clinic' },
  { value: 'hvac', label: 'HVAC / Plumbing' },
  { value: 'law', label: 'Law Firm' },
  { value: 'real_estate', label: 'Real Estate' },
  { value: 'vet', label: 'Veterinary Clinic' },
  { value: 'auto', label: 'Auto Shop / Detailing' },
  { value: 'fitness', label: 'Fitness / Personal Training' },
  { value: 'restaurant', label: 'Restaurant / Cafe' },
  { value: 'cleaning', label: 'Cleaning Service' },
  { value: 'photography', label: 'Photography / Videography' },
  { value: 'consulting', label: 'Consulting / Coaching' },
  { value: 'currency_exchange', label: 'Currency Exchange / FX (FINTRAC MSB)' },
  { value: 'other', label: 'Other' },
];

// Multi-select goals — packaged so we can recommend a plan from the mix.
const GOALS = [
  { value: 'BOOKING', label: 'Book appointments automatically', tier: 'starter' },
  { value: 'QUALIFICATION', label: 'Qualify and capture leads', tier: 'starter' },
  { value: 'RECEPTIONIST_SUPPORT', label: 'Answer calls & route inquiries', tier: 'starter' },
  { value: 'SMS_FOLLOWUP', label: 'SMS follow-ups & reminders', tier: 'growth' },
  { value: 'WEBSITE', label: 'Get a simple website built for me', tier: 'growth' },
  { value: 'MULTI_LOCATION', label: 'Handle calls across multiple locations', tier: 'growth' },
  { value: 'AFTER_HOURS', label: '24/7 after hours coverage', tier: 'starter' },
  { value: 'CALENDAR_SYNC', label: 'Sync with my Google / Outlook calendar', tier: 'growth' },
];

const PLANS = [
  { id: 'payg',    name: 'Pay As You Go', price: 0, minutes: '$0.45/min · $0.05/text', desc: 'No monthly fee — load credits, pay only for what Ava uses' },
  { id: 'starter', name: 'Starter', price: 179.99, minutes: '250 min/mo', desc: 'AI receptionist + CRM + booking calendar' },
  { id: 'growth',  name: 'Growth',  price: 279.99, minutes: '500 min/mo', desc: 'Everything + SMS, analytics, calendar sync', popular: true },
  { id: 'agency',  name: 'Agency · White-label',  price: 1497, minutes: '2,000 min/mo', desc: 'Multi-location, white-label, priority support' },
];

// Maps the signup industry value to the canonical niche string the dashboard's
// INDUSTRY_CONFIG (and the backend Vapi prompt) expects. Anything not in this
// map falls back to 'other'.
const NICHE_MAP = {
  // restaurant
  restaurant: 'restaurant',
  // medical / clinic / dental → clinic
  medical: 'clinic',
  clinic: 'clinic',
  dental: 'clinic',
  // contractor (incl. HVAC / plumbing / general_contractor)
  contractor: 'contractor',
  general_contractor: 'contractor',
  hvac: 'contractor',
  // salon
  salon: 'salon',
  hair_salon: 'salon',
  // legal / law firm
  legal: 'legal',
  law: 'legal',
  law_firm: 'legal',
  // real estate
  real_estate: 'real_estate',
  // currency exchange
  currency_exchange: 'currency_exchange',
};

function normalizeNiche(industry) {
  const key = (industry || '').toString().trim().toLowerCase().replace(/\s+/g, '_');
  return NICHE_MAP[key] || 'other';
}

const inputClass = "w-full bg-white/[0.05] border border-white/10 rounded-xl px-4 py-3 text-white text-sm focus:border-blue-500 focus:bg-white/[0.07] focus:ring-1 focus:ring-blue-500/40 transition-all placeholder:text-slate-600";
const selectClass = "w-full bg-white/[0.05] border border-white/10 rounded-xl px-4 py-3 text-white text-sm focus:border-blue-500 focus:bg-white/[0.07] focus:ring-1 focus:ring-blue-500/40 transition-all appearance-none";

function emailValid(email) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email || '');
}

function recommendPlan({ goals, locationCount, wantsSms }) {
  // Multi-location pivot (Feb 2026): Growth is single-location only.
  // 2-5 locations = Agency, 6+ = Enterprise.
  if (locationCount === '6_PLUS') return 'enterprise';
  if (locationCount === '2_5') return 'agency';
  if (wantsSms) return 'growth';
  const growthGoals = GOALS.filter(g => g.tier === 'growth').map(g => g.value);
  if ((goals || []).some(g => growthGoals.includes(g))) return 'growth';
  return 'starter';
}

export default function SignupPage() {
  const [step, setStep] = useState(1);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { register, formatError } = useAuth();
  const navigate = useNavigate();

  // Step 1: Account
  const [account, setAccount] = useState({ name: '', email: '', password: '', phone: '' });
  // Step 2: Pre-onboarding (multi-select goals + location/SMS)
  const [preonboard, setPreonboard] = useState({
    industry: '',
    goals: [],            // multi-select
    locationCount: '',    // required: must be picked
    wantsSms: null,       // required: explicit yes/no
  });
  // Step 3: Business details (all required)
  const [business, setBusiness] = useState({
    businessName: '',
    displayName: '',
    address: '',
    timezone: '',
    fintracNumber: '',
  });
  // Step 4: Plan
  const isCurrencyExchange = preonboard.industry === 'currency_exchange';
  const isMultiLocation = preonboard.locationCount === '2_5' || preonboard.locationCount === '6_PLUS';
  // Multi-location pivot: 2-5 = Agency, 6+ = Enterprise.
  const recommended = useMemo(() => {
    if (isCurrencyExchange) return 'custom';
    if (preonboard.locationCount === '6_PLUS') return 'enterprise';
    if (preonboard.locationCount === '2_5') return 'agency';
    return recommendPlan(preonboard);
  }, [preonboard, isCurrencyExchange]);
  const [plan, setPlan] = useState('');

  // When user reaches step 4 the first time, pre-select the recommended plan.
  // We do that lazily inside the step-4 render so it stays reactive to changes in step 2/3.
  const planValue = plan || recommended;

  const totalSteps = 4;

  const toggleGoal = (val) => {
    setPreonboard(prev => {
      const exists = prev.goals.includes(val);
      return { ...prev, goals: exists ? prev.goals.filter(g => g !== val) : [...prev.goals, val] };
    });
  };

  const handleSubmit = async () => {
    setError('');
    setLoading(true);
    try {
      await register({
        name: account.name.trim(),
        email: account.email.trim().toLowerCase(),
        password: account.password,
        phone: account.phone.trim(),
        business_name: business.businessName.trim(),
        business_type: normalizeNiche(preonboard.industry),
        plan: planValue,
        // Extended signup metadata
        display_name: business.displayName.trim(),
        timezone: business.timezone,
        location_count: preonboard.locationCount,
        business_goals: preonboard.goals,
        wants_sms: !!preonboard.wantsSms,
        address: business.address.trim(),
        fintrac_number: business.fintracNumber.trim(),
      });
      setStep(5); // success
      setTimeout(() => navigate('/onboarding'), 1500);
    } catch (err) {
      setError(formatError(err.response?.data?.detail) || err.message);
      setLoading(false);
    }
  };

  // Step-by-step strict validation, every field is mandatory.
  const stepError = () => {
    if (step === 1) {
      if (!account.name.trim()) return 'Please enter your full name.';
      if (!emailValid(account.email)) return 'Please enter a valid work email.';
      if ((account.password || '').length < 8) return 'Password must be at least 8 characters.';
      if (!account.phone.trim()) return 'Please enter your phone number.';
      return '';
    }
    if (step === 2) {
      if (!preonboard.industry) return 'Please select your industry.';
      if (!preonboard.goals.length) return 'Pick at least one goal so we can tailor Ava for you.';
      if (!preonboard.locationCount) return 'Please tell us how many locations you have.';
      if (preonboard.wantsSms === null) return 'Let us know if you need SMS follow-ups.';
      return '';
    }
    if (step === 3) {
      if (!business.businessName.trim()) return 'Business name is required.';
      if (!business.displayName.trim()) return 'Display name (what callers hear) is required.';
      if (!business.address.trim()) return 'Business address is required.';
      if (!business.timezone) return 'Please pick a timezone.';
      if (isCurrencyExchange && !business.fintracNumber.trim()) return 'FINTRAC MSB number is required for Currency Exchange businesses.';
      return '';
    }
    if (step === 4) {
      if (!planValue) return 'Please choose a plan.';
      if (isMultiLocation && (planValue === 'payg' || planValue === 'starter' || planValue === 'growth')) return 'Pay As You Go, Starter and Growth are single-location plans. Please pick Agency (up to 5 locations) or Enterprise (6+).';
      return '';
    }
    return '';
  };

  const tryNext = () => {
    const err = stepError();
    if (err) { setError(err); return; }
    setError('');
    setStep(step + 1);
  };

  return (
    <div className="min-h-screen relative bg-black overflow-hidden flex items-center justify-center px-6 py-12">
      {/* radial blue glow background */}
      <div className="pointer-events-none absolute inset-0 opacity-50"
           style={{ background: 'radial-gradient(60% 50% at 50% 30%, rgba(37,99,235,0.18) 0%, rgba(0,0,0,0) 60%)' }} />
      <div className="relative w-full max-w-lg">
        <div className="text-center mb-7">
          <Link to="/" className="font-heading font-semibold text-white text-lg inline-block mb-6">NextBell</Link>
          {step < 5 && (
            <>
              <h1 data-testid="signup-page-title" className="font-heading text-2xl md:text-3xl font-semibold text-white mb-1.5 tracking-tight">
                {step === 1 ? 'Create your account' : step === 2 ? 'Tell us about your business' : step === 3 ? 'Business details' : 'Choose your plan'}
              </h1>
              <p className="text-slate-500 text-sm">Step {step} of {totalSteps} · all fields are required</p>
              <div className="flex gap-1.5 justify-center mt-3">
                {[...Array(totalSteps)].map((_, i) => (
                  <div key={i} className={`h-1 w-10 rounded-full transition-colors ${i < step ? 'bg-blue-500' : 'bg-white/10'}`} />
                ))}
              </div>
            </>
          )}
        </div>

        <div className="bg-white/[0.04] backdrop-blur-xl border border-white/10 rounded-3xl p-7 md:p-9 shadow-[0_30px_80px_-30px_rgba(37,99,235,0.45)]">

        {error && <div data-testid="signup-error" className="bg-red-500/10 border border-red-500/30 rounded-xl px-4 py-3 text-red-300 text-sm mb-4">{error}</div>}

        {/* Step 1: Account */}
        {step === 1 && (
          <div className="space-y-4" data-testid="signup-step-1">
            <Field label="Full name" required>
              <input data-testid="signup-name-input" type="text" placeholder="Sarah Smith" value={account.name} onChange={e => setAccount({...account, name: e.target.value})} className={inputClass} />
            </Field>
            <Field label="Work email" required>
              <input data-testid="signup-email-input" type="email" placeholder="sarah@yourbusiness.com" value={account.email} onChange={e => setAccount({...account, email: e.target.value})} className={inputClass} />
            </Field>
            <Field label="Password" required hint="Minimum 8 characters">
              <input data-testid="signup-password-input" type="password" placeholder="••••••••" value={account.password} onChange={e => setAccount({...account, password: e.target.value})} className={inputClass} />
            </Field>
            <Field label="Phone number" required>
              <input data-testid="signup-phone-input" type="tel" placeholder="+1 (555) 123-4567" value={account.phone} onChange={e => setAccount({...account, phone: e.target.value})} className={inputClass} />
            </Field>
          </div>
        )}

        {/* Step 2: Pre-onboarding */}
        {step === 2 && (
          <div className="space-y-5" data-testid="signup-step-2">
            <Field label="What industry are you in?" required>
              <select data-testid="signup-industry-select" value={preonboard.industry} onChange={e => setPreonboard({...preonboard, industry: e.target.value})} className={selectClass}>
                <option value="">Select your industry</option>
                {INDUSTRIES.map(i => <option key={i.value} value={i.value}>{i.label}</option>)}
              </select>
            </Field>

            <Field label="What do you want Ava to handle?" required hint="Pick all that apply, this helps us recommend the right plan.">
              <div className="space-y-2" data-testid="signup-goals">
                {GOALS.map(g => {
                  const selected = preonboard.goals.includes(g.value);
                  return (
                    <button
                      type="button"
                      key={g.value}
                      data-testid={`goal-${g.value.toLowerCase()}`}
                      onClick={() => toggleGoal(g.value)}
                      className={`w-full text-left rounded-xl px-4 py-3 border text-sm transition-all flex items-center gap-3 ${selected ? 'border-blue-500 bg-blue-500/100/15 text-blue-300' : 'border-white/10 bg-white/[0.03] text-slate-300 hover:border-white/30 hover:bg-white/[0.06]'}`}>
                      <span className={`w-4 h-4 rounded border flex-shrink-0 flex items-center justify-center ${selected ? 'bg-blue-600 border-blue-600' : 'border-white/20'}`}>
                        {selected && <Check className="w-3 h-3 text-white" />}
                      </span>
                      <span>{g.label}</span>
                      {g.tier === 'growth' && <span className="ml-auto text-[10px] uppercase tracking-wide bg-blue-500/15 text-blue-300 px-2 py-0.5 rounded-full">Growth</span>}
                    </button>
                  );
                })}
              </div>
            </Field>

            <Field label="How many locations?" required>
              <select data-testid="signup-locations-select" value={preonboard.locationCount} onChange={e => setPreonboard({...preonboard, locationCount: e.target.value})} className={selectClass}>
                <option value="">Select location count</option>
                <option value="1">1 location</option>
                <option value="2_5">2 to 5 locations</option>
                <option value="6_PLUS">6+ locations</option>
              </select>
            </Field>

            <Field label="Do you need SMS follow-ups?" required>
              <div className="grid grid-cols-2 gap-3" data-testid="signup-sms-toggle">
                <button type="button" onClick={() => setPreonboard({...preonboard, wantsSms: true})}
                  className={`rounded-xl px-4 py-3 border text-sm transition-all ${preonboard.wantsSms === true ? 'border-blue-500 bg-blue-500/100/15 text-blue-300' : 'border-white/10 bg-white/[0.03] text-slate-300 hover:border-white/30 hover:bg-white/[0.06]'}`}>
                  Yes, I want SMS
                </button>
                <button type="button" onClick={() => setPreonboard({...preonboard, wantsSms: false})}
                  className={`rounded-xl px-4 py-3 border text-sm transition-all ${preonboard.wantsSms === false ? 'border-blue-500 bg-blue-500/100/15 text-blue-300' : 'border-white/10 bg-white/[0.03] text-slate-300 hover:border-white/30 hover:bg-white/[0.06]'}`}>
                  No SMS for now
                </button>
              </div>
            </Field>
          </div>
        )}

        {/* Step 3: Business details */}
        {step === 3 && (
          <div className="space-y-4" data-testid="signup-step-3">
            <Field label="Business name" required>
              <input data-testid="signup-business-name" type="text" placeholder="e.g., Luxe Hair Studio" value={business.businessName} onChange={e => setBusiness({...business, businessName: e.target.value})} className={inputClass} />
            </Field>
            <Field label="Display name" required hint="What Ava says when she answers (e.g. “Luxe Hair”).">
              <input data-testid="signup-display-name" type="text" placeholder="e.g., Luxe Hair" value={business.displayName} onChange={e => setBusiness({...business, displayName: e.target.value})} className={inputClass} />
            </Field>
            <Field label="Business address" required>
              <AddressAutocompleteInput
                value={business.address}
                onChange={(v) => setBusiness({ ...business, address: v })}
                onSelect={(addr) => setBusiness({
                  ...business,
                  address: addr.formatted,
                  address_line1: addr.line1,
                  address_city: addr.city,
                  address_state: addr.state,
                  address_postal: addr.postal,
                  address_country: addr.country,
                  address_lat: addr.lat,
                  address_lng: addr.lng,
                })}
                className={inputClass}
                inputProps={{ 'data-testid': 'signup-address', placeholder: 'Start typing your address…' }}
              />
            </Field>
            <Field label="Timezone" required>
              <select data-testid="signup-timezone" value={business.timezone} onChange={e => setBusiness({...business, timezone: e.target.value})} className={selectClass}>
                <option value="">Select your timezone</option>
                <option value="America/New_York">Eastern (New York)</option>
                <option value="America/Chicago">Central (Chicago)</option>
                <option value="America/Denver">Mountain (Denver)</option>
                <option value="America/Los_Angeles">Pacific (Los Angeles)</option>
                <option value="America/Vancouver">Pacific (Vancouver)</option>
                <option value="America/Toronto">Eastern (Toronto)</option>
              </select>
            </Field>
            {isCurrencyExchange && (
              <Field label="FINTRAC MSB number" required hint="Your Money Service Business registration ID. Required by Canadian regulation for currency exchange operators.">
                <input data-testid="signup-fintrac" type="text" placeholder="e.g., M12345678" value={business.fintracNumber} onChange={e => setBusiness({...business, fintracNumber: e.target.value})} className={inputClass} />
              </Field>
            )}
            <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-4">
              <p className="text-xs text-blue-700 font-medium mb-1">Your AI won't go live until onboarding finishes</p>
              <p className="text-xs text-blue-600">After signup, you'll answer a few more questions to tailor your AI assistant to your business.</p>
            </div>
          </div>
        )}

        {/* Step 4: Plan */}
        {step === 4 && (
          <div className="space-y-3" data-testid="signup-step-4">
            {isCurrencyExchange ? (
              <>
                <div className="bg-amber-500/10 border border-amber-400/30 rounded-xl px-4 py-3 text-sm text-amber-100">
                  Currency Exchange businesses run on a <strong className="text-amber-50">Custom plan</strong>. We&apos;ll set pricing based on your volume after a short call. You&apos;ll get the standard dashboard with Ava trained on your FX business.
                </div>
                <button type="button" data-testid="signup-plan-custom" onClick={() => setPlan('custom')}
                  className={`w-full text-left rounded-xl p-5 border transition-all ${planValue === 'custom' ? 'border-blue-400 bg-blue-500/15 shadow-[0_0_0_1px_rgba(96,165,250,0.4)]' : 'border-white/15 bg-white/[0.07] hover:border-white/30 hover:bg-white/[0.10]'}`}>
                  <div className="flex items-center justify-between">
                    <div>
                      <span className="text-white font-semibold">Custom (FX)</span>
                      <span className="ml-2 text-[10px] bg-blue-500/20 text-blue-200 border border-blue-400/30 px-2 py-0.5 rounded-full">Recommended for you</span>
                      <p className="text-slate-200 text-xs mt-1">Standard dashboard with Ava trained on your FX business. Pricing arranged per business.</p>
                      <p className="text-slate-300/80 text-[11px] mt-1">FINTRAC compliant, 24/7 AI receptionist</p>
                    </div>
                    <div className="text-right">
                      <span className="text-2xl font-heading font-semibold text-white">Custom</span>
                      <span className="text-slate-300 text-xs block">contact us</span>
                    </div>
                  </div>
                </button>
                <p className="text-xs text-slate-300 text-center mt-2">7 day free trial. We&apos;ll reach out within 24h to confirm pricing.</p>
              </>
            ) : (
              <>
                <div className="bg-blue-500/10 border border-blue-400/30 rounded-xl px-4 py-3 text-sm text-blue-100">
                  Based on your answers we recommend the <strong className="capitalize text-blue-50">{recommended}</strong> plan.
                </div>
                {PLANS.map(p => {
                  const isRec = p.id === recommended;
                  const isLocked = isMultiLocation && (p.id === 'payg' || p.id === 'starter' || p.id === 'growth');
                  return (
                    <button type="button" key={p.id} data-testid={`signup-plan-${p.id}`}
                      onClick={() => { if (!isLocked) setPlan(p.id); }}
                      disabled={isLocked}
                      className={`w-full text-left rounded-xl p-5 border transition-all ${isLocked ? 'border-white/5 bg-white/[0.02] opacity-50 cursor-not-allowed' : planValue === p.id ? 'border-blue-400 bg-blue-500/15 shadow-[0_0_0_1px_rgba(96,165,250,0.4)]' : 'border-white/15 bg-white/[0.07] hover:border-white/30 hover:bg-white/[0.10]'}`}>
                      <div className="flex items-center justify-between gap-4">
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center flex-wrap gap-2">
                            <span className="text-white font-semibold">{p.name}</span>
                            {p.popular && !isLocked && <span className="text-[10px] bg-blue-500/90 text-white px-2 py-0.5 rounded-full">Popular</span>}
                            {isRec && !isLocked && <span className="text-[10px] bg-blue-500/20 text-blue-200 border border-blue-400/30 px-2 py-0.5 rounded-full">Recommended for you</span>}
                            {isLocked && <span className="text-[10px] bg-white/10 text-slate-300 border border-white/15 px-2 py-0.5 rounded-full">Single-location only</span>}
                          </div>
                          <p className="text-slate-200 text-xs mt-1.5">{p.desc}</p>
                          <p className="text-slate-300/80 text-[11px] mt-1">{p.minutes}</p>
                        </div>
                        <div className="text-right shrink-0">
                          {p.id === 'payg' ? (
                            <>
                              <span className="text-2xl font-heading font-semibold text-white">$0</span>
                              <span className="text-slate-300 text-xs">/mo</span>
                              <p className="text-[10px] text-slate-400 mt-0.5">pay per use</p>
                            </>
                          ) : (
                            <>
                              <span className="text-2xl font-heading font-semibold text-white">${p.price}</span>
                              <span className="text-slate-300 text-xs">/mo</span>
                            </>
                          )}
                        </div>
                      </div>
                    </button>
                  );
                })}
                <p className="text-xs text-slate-300 text-center mt-2">7 day free trial. Cancel anytime.</p>
              </>
            )}
          </div>
        )}

        {/* Step 5: Success */}
        {step === 5 && (
          <div data-testid="signup-success" className="text-center py-8">
            <div className="w-14 h-14 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Check className="w-7 h-7 text-green-600" />
            </div>
            <h2 className="font-heading text-2xl font-semibold text-white mb-2">Welcome to NextBell!</h2>
            <p className="text-slate-400 text-sm">Setting up your dashboard...</p>
          </div>
        )}

        {/* Navigation */}
        {step < 5 && (
          <div className="mt-6 flex gap-3">
            {step > 1 && (
              <button type="button" onClick={() => { setError(''); setStep(step - 1); }} className="flex-1 bg-white/[0.05] hover:bg-white/[0.08] border border-white/10 text-slate-200 rounded-full py-3 text-sm font-medium flex items-center justify-center gap-1">
                <ArrowLeft className="w-4 h-4" /> Back
              </button>
            )}
            {step < 4 ? (
              <button type="button" data-testid="signup-next-btn" onClick={tryNext}
                className="flex-1 bg-blue-600 hover:bg-blue-500 text-white rounded-full py-3 text-sm font-medium flex items-center justify-center gap-1 shadow-[0_0_30px_rgba(37,99,235,0.35)]">
                Next <ArrowRight className="w-4 h-4" />
              </button>
            ) : (
              <button type="button" data-testid="signup-submit-btn" onClick={() => {
                  const err = stepError(); if (err) { setError(err); return; } handleSubmit();
                }} disabled={loading}
                className="flex-1 bg-blue-600 hover:bg-blue-500 text-white rounded-full py-3 text-sm font-medium disabled:opacity-50 shadow-[0_0_30px_rgba(37,99,235,0.35)]">
                {loading ? 'Creating...' : 'Start free trial'}
              </button>
            )}
          </div>
        )}

        </div>{/* /glass card */}

        {step < 5 && (
          <p className="text-center text-sm text-slate-400 mt-6">
            Already have an account? <Link to="/login" className="text-blue-400 hover:text-blue-300 font-medium">Log in</Link>
          </p>
        )}
      </div>
    </div>
  );
}

function Field({ label, required, hint, children }) {
  return (
    <div>
      <label className="text-xs uppercase tracking-wide text-slate-500 font-medium mb-1.5 block">
        {label}{required && <span className="text-red-400 ml-0.5">*</span>}
      </label>
      {hint && <p className="text-[11px] text-slate-500 mb-1.5">{hint}</p>}
      {children}
    </div>
  );
}
